import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, Upload, X, ImageIcon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScoreBar({ value, label, color = "pink" }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const colorMap = {
    pink: "from-cyan-500 to-lime-400",
    cyan: "from-cyan-500 to-blue-500",
    emerald: "from-emerald-500 to-teal-500",
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-zinc-300 font-medium">{label}</span>
        <span className="text-xs text-zinc-500 font-mono">{pct}/100</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${colorMap[color]} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function StoryCheckPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { summary: dna } = useBrandDnaSummary();

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Bitte ein Bild hochladen"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Bild zu groß (max 8 MB)"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const run = async () => {
    if (!file) { toast.error("Bitte erst einen Story-Screenshot hochladen"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("goal", goal.trim() || (dna?.core_promise || "Engagement & DMs"));
      const { data } = await axios.post(`${API}/analyse/story-check`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data.ai_analysis);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Story-Check fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="story-check-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <ImageIcon className="w-5 h-5 text-cyan-400" />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Story-Check</h1>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">Vision-AI</span>
        </div>

        {!result && (
          <div className="space-y-4">
            {dna && <BrandDnaCard summary={dna} variant="hint" />}

            <p className="text-sm text-zinc-400">
              Lade einen Screenshot deiner geplanten Story hoch — die KI bewertet Hook, Lesbarkeit,
              CTA, Sticker-Nutzung und gibt dir konkret-actionable Tipps zum Optimieren.
            </p>

            {/* Drop zone */}
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
              className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all ${
                preview ? "border-cyan-500/40 bg-cyan-500/5" : "border-zinc-700 hover:border-cyan-500/40 hover:bg-zinc-900/40"
              } p-6 flex flex-col items-center gap-3 min-h-[260px] justify-center`}
              data-testid="story-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
                data-testid="story-file-input"
              />
              {preview ? (
                <>
                  <img src={preview} alt="Story preview" className="max-h-[280px] rounded-xl border border-zinc-800" />
                  <button
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="absolute top-2 right-2 p-1.5 rounded-md bg-black/60 hover:bg-black/80 text-zinc-300"
                    data-testid="story-clear-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-zinc-400">{file?.name} · {(file?.size / 1024).toFixed(0)} KB</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                    <Upload className="w-6 h-6 text-cyan-300" />
                  </div>
                  <p className="text-sm font-semibold text-white">Story-Screenshot hochladen</p>
                  <p className="text-xs text-zinc-500">PNG/JPG bis 8 MB · drag & drop oder klicken</p>
                </>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Ziel der Story (optional)
              </label>
              <Input
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={dna?.core_promise || "z.B. DMs auslösen / Link-Klicks / Umfrage-Engagement"}
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="story-goal-input"
              />
            </div>

            <Button
              onClick={run}
              disabled={loading || !file}
              className="w-full bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 text-white h-11"
              data-testid="story-run-btn"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Story wird analysiert (~10-15s)…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Story-Check starten</>}
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="story-result">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-lime-400/5 border border-cyan-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
                  <span className="text-xl font-bold text-cyan-300">{result.overall_score || 0}</span>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Gesamt-Score</p>
                  <p className="text-sm font-semibold text-white">{result.overall_score >= 80 ? "Stark" : result.overall_score >= 60 ? "Solide" : result.overall_score >= 40 ? "Verbesserbar" : "Schwach"}</p>
                </div>
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed">{result.verdict}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 space-y-3">
                <ScoreBar value={result.hook_score} label="Hook" color="pink" />
                <p className="text-[11px] text-zinc-400 leading-relaxed">{result.hook_critique}</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 space-y-3">
                <ScoreBar value={result.readability_score} label="Lesbarkeit" color="cyan" />
                {result.readability_issues?.length > 0 && (
                  <ul className="text-[11px] text-zinc-400 space-y-0.5">
                    {result.readability_issues.map((it, i) => <li key={i}>▸ {it}</li>)}
                  </ul>
                )}
              </div>
            </div>

            <div className={`p-4 rounded-xl border ${result.cta_present ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
              <p className={`text-sm font-semibold mb-1 flex items-center gap-2 ${result.cta_present ? "text-emerald-300" : "text-amber-300"}`}>
                {result.cta_present ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {result.cta_present ? "Call-to-Action erkannt" : "CTA fehlt oder schwach"}
              </p>
              <p className="text-xs text-zinc-300 leading-relaxed">{result.cta_critique}</p>
            </div>

            {result.sticker_usage?.length > 0 && (
              <div className="p-3 rounded-lg bg-zinc-900 border border-zinc-800">
                <p className="text-xs font-medium text-zinc-300 mb-2">Verwendete Sticker:</p>
                <div className="flex flex-wrap gap-1.5">
                  {result.sticker_usage.map((s, i) => (
                    <span key={i} className="text-[10px] px-2 py-1 rounded bg-zinc-800 text-zinc-300">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {result.sticker_recommendation && (
              <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <p className="text-xs font-semibold text-cyan-300 mb-1">Sticker-Empfehlung</p>
                <p className="text-xs text-zinc-300 leading-relaxed">{result.sticker_recommendation}</p>
              </div>
            )}

            {result.rewrite_suggestion && (
              <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/30">
                <p className="text-xs font-semibold text-cyan-300 mb-2">Optimierte Story-Variante</p>
                <p className="text-sm text-zinc-100 italic leading-relaxed whitespace-pre-wrap">{result.rewrite_suggestion}</p>
              </div>
            )}

            {result.improvement_checklist?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-sm font-semibold text-white mb-3">Checkliste — was du als nächstes machst:</p>
                <ul className="space-y-2">
                  {result.improvement_checklist.map((it, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className={`flex-shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded mt-0.5 ${
                        it.priority === "hoch"
                          ? "bg-red-500/15 text-red-300 border border-red-500/30"
                          : it.priority === "mittel"
                          ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          : "bg-zinc-700 text-zinc-300"
                      }`}>{it.priority}</span>
                      <div className="text-xs text-zinc-200 leading-relaxed">
                        <p className="font-medium">{it.task}</p>
                        {it.impact && <p className="text-[10px] text-zinc-500 mt-0.5">→ {it.impact}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={reset} variant="outline" className="w-full border-zinc-700 text-zinc-400" data-testid="story-new-btn">
              Andere Story prüfen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
